import { useCallback, useEffect, useMemo, useState } from 'react'

import { CustomerNode, useAllCustomersQuery } from '@/apollo/graphql'
import { IOption, IPageInfo, Maybe } from '@/types'
import { customerDisplayLabel, getFetchMoreStatus } from '@/utils'

interface IUseSelect {
  initial?: Maybe<Partial<CustomerNode>>
}

const pageSize = 15

const initialPageInfo: IPageInfo = {
  hasNextPage: false,
  offset: 0,
  fetchMoreStatus: 'loading',
}

export const useSelect = ({ initial }: IUseSelect) => {
  const [customers, setCustomers] = useState<Partial<CustomerNode>[]>([])
  const [pageInfo, setPageInfo] = useState<IPageInfo>(initialPageInfo)
  const [search, setSearch] = useState<string>('')

  const { loading, networkStatus } = useAllCustomersQuery({
    fetchPolicy: 'network-only',
    variables: {
      offset: pageInfo?.offset || 0,
      first: pageSize,
      nameIncludes: search,
    },
    onCompleted: res => {
      if (res.allCustomers?.edges) {
        const _customers: Partial<CustomerNode>[] = []
        res.allCustomers.edges.forEach(e => {
          if (e?.node) _customers.push(e.node)
        })

        setCustomers(prev => [...prev, ..._customers])
        setPageInfo(prev => ({
          ...prev,
          hasNextPage: res.allCustomers?.pageInfo.hasNextPage,
        }))
      }
    },
  })

  const customerOptions: IOption<string>[] = useMemo(() => {
    let exists = false
    const opts = (customers || []).map(s => {
      if (!exists && initial?.id === s.id) {
        exists = true
      }

      return {
        label: customerDisplayLabel(s),
        value: s.id || '',
      }
    })
    if (!exists && initial && !search) {
      opts.unshift({
        label: customerDisplayLabel(initial),
        value: initial.id || '',
      })
    }
    return opts
  }, [customers, initial, search])

  useEffect(() => {
    setPageInfo(prev => ({
      ...prev,
      fetchMoreStatus: getFetchMoreStatus({
        networkStatus,
        hasNextPage: prev?.hasNextPage,
        withForce: true,
      }),
    }))
  }, [networkStatus])

  const onFetchMore = useCallback(() => {
    setPageInfo(prev => ({ ...prev, offset: customers.length }))
  }, [customers?.length])

  const onSearch = useCallback(
    (text: string) => {
      if (search !== text) {
        setCustomers([])
      }
      setSearch(text)
      setPageInfo(initialPageInfo)
    },
    [search],
  )

  return {
    loading,
    customerOptions,
    pageInfo,
    onFetchMore,
    onSearch,
  }
}
